import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import {
  dateToPlainDate,
  plainDateToDate,
} from "@redotech/redo-web/timetable/common/utils/date-conversion";
import { InputSchema, groupInput, input } from "@redotech/ui/form";
import { memo, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { AuthContext } from "../../../app/auth";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import {
  OutboundLabelForm,
  usersWhoCanEditMerchantFees,
} from "../outbound-labels";

const toDate = (value: Date | string): Date => {
  return value instanceof Date ? value : new Date(value);
};

const toISOString = (value: Date | string | undefined): string | null => {
  if (!value) {
    return null;
  }
  return toDate(value).toISOString();
};

const getStartOfCurrentBillingPeriod = (): Temporal.PlainDate => {
  return dateToPlainDate(new Date()).with({ day: 1 });
};

export interface MerchantCarrierFeeInitialState {
  omsCanBeEnabled: boolean;
  wmsCanBeEnabled: boolean;
  initialOmsFee: number;
  initialWmsFee: number;
  initialLabelFee: number;
  initialTiktokLabelFee: number;
  initialOmsTurnedOnAt: Date | undefined;
  initialWmsTurnedOnAt: Date | undefined;
}

export const merchantCarrierFeeSettingsForm = groupInput({
  merchantCarrierAccountLabelFee: groupInput({
    amount: input<number>(),
    tiktokShippingAmount: input<number>(),
  }),
  monthlySubscriptionFee: groupInput({
    amount: input<number>(),
    turnedOnAt: input<Date | undefined>(),
  }),
  wmsSubscriptionFee: groupInput({
    amount: input<number>(),
    turnedOnAt: input<Date | undefined>(),
  }),
});

export type MerchantCarrierFeeSettingsForm = InputSchema.Input<
  typeof merchantCarrierFeeSettingsForm
>;

export type MerchantCarrierFeeSettingsValue = InputSchema.Value<
  typeof merchantCarrierFeeSettingsForm
>;

export const merchantCarrierFeeSettingsDefault: MerchantCarrierFeeSettingsValue =
  {
    merchantCarrierAccountLabelFee: { amount: 0, tiktokShippingAmount: 0 },
    monthlySubscriptionFee: { amount: 0, turnedOnAt: undefined },
    wmsSubscriptionFee: { amount: 0, turnedOnAt: undefined },
  };

function isDateInPast(date: Date | string | undefined): boolean {
  if (!date) {
    return false;
  }
  const d = toDate(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

export const MerchantCarrierFeeSettingsCard = memo(
  function MerchantCarrierFeeSettingsCard({
    input,
    initialState,
  }: {
    input: OutboundLabelForm;
    initialState: MerchantCarrierFeeInitialState;
  }) {
    const params = useParams();
    const teamId = params.teamId!;
    const auth = useContext(AuthContext);
    const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
    const canEditFees =
      auth && usersWhoCanEditMerchantFees.includes(auth.email);

    const { fees } = input.inputs;
    const subscriptionFee = fees.inputs.monthlySubscriptionFee.inputs;
    const merchantCarrierFee =
      fees.inputs.merchantCarrierAccountLabelFee.inputs;
    const perLabelFee = merchantCarrierFee.amount;
    const tiktokLabelFee = merchantCarrierFee.tiktokShippingAmount;
    const wmsSubscriptionFee = fees.inputs.wmsSubscriptionFee.inputs;

    const omsToggleDisabled = !canEditFees && !initialState.omsCanBeEnabled;
    const wmsToggleDisabled = !canEditFees && !initialState.wmsCanBeEnabled;

    const overrides = useMemo(
      () => ({
        omsTurnedOnAt: toISOString(subscriptionFee.turnedOnAt.value),
        omsAmount: subscriptionFee.amount.value,
        wmsTurnedOnAt: toISOString(wmsSubscriptionFee.turnedOnAt.value),
        wmsAmount: wmsSubscriptionFee.amount.value,
      }),
      [
        subscriptionFee.turnedOnAt.value,
        subscriptionFee.amount.value,
        wmsSubscriptionFee.turnedOnAt.value,
        wmsSubscriptionFee.amount.value,
      ],
    );

    const billingPreviewLoad = useLoad(
      async (signal) => {
        const result = await rpcClient.getSubscriptionFeeBillingPreview(
          { teamId, overrides },
          { signal },
        );
        return { ...result, nextBillingDate: new Date(result.nextBillingDate) };
      },
      [teamId, overrides, rpcClient],
    );

    const billingPreview = billingPreviewLoad.value;

    return (
      <Card title="Monthly recurring fees">
        <Flex dir="column" gap="xl">
          <RedoIncrementDecrement
            label="Merchant Carrier Account Label Fee"
            min={!canEditFees ? initialState.initialLabelFee : 0}
            placeholder="0"
            prefix="$"
            setValue={perLabelFee.setValue}
            step={0.01}
            value={perLabelFee.value}
          />
          <RedoIncrementDecrement
            label="TikTok Merchant Carrier Account Label Fee"
            min={!canEditFees ? initialState.initialTiktokLabelFee : 0}
            placeholder="0"
            prefix="$"
            setValue={tiktokLabelFee.setValue}
            step={0.01}
            value={tiktokLabelFee.value}
          />
          <Flex dir="row" gap="2xl">
            <Flex dir="column" gap="2xl">
              {!canEditFees && (
                <Text fontSize="xs" textColor="tertiary">
                  Only approved users can change billing settings
                </Text>
              )}
              <RedoToggle
                disabled={omsToggleDisabled}
                label="Enable OMS monthly subscription fee"
                setValue={(value) => {
                  subscriptionFee.turnedOnAt.setValue(
                    value ? new Date() : undefined,
                  );
                  if (!value) {
                    subscriptionFee.amount.setValue(0);
                  }
                }}
                size="sm"
                value={!!subscriptionFee.turnedOnAt.value}
              />
              {!!subscriptionFee.turnedOnAt.value && (
                <Flex dir="column" gap="md">
                  <Flex align="center" dir="row" gap="md">
                    <Text fontSize="sm">Begin billing on:</Text>
                    <RedoSingleDatePicker
                      buttonSize="sm"
                      date={dateToPlainDate(
                        toDate(subscriptionFee.turnedOnAt.value),
                      )}
                      minDate={getStartOfCurrentBillingPeriod()}
                      setDate={(date) => {
                        subscriptionFee.turnedOnAt.setValue(
                          plainDateToDate(date),
                        );
                      }}
                    />
                  </Flex>
                  {isDateInPast(initialState.initialOmsTurnedOnAt) &&
                    subscriptionFee.turnedOnAt.value &&
                    toDate(subscriptionFee.turnedOnAt.value).getTime() !==
                      toDate(initialState.initialOmsTurnedOnAt!).getTime() && (
                      <Text fontSize="xs" textColor="error">
                        Original start date was{" "}
                        {toDate(
                          initialState.initialOmsTurnedOnAt!,
                        ).toLocaleDateString()}
                      </Text>
                    )}
                  <RedoIncrementDecrement
                    disabled={omsToggleDisabled}
                    label="Monthly Subscription Fee"
                    min={omsToggleDisabled ? initialState.initialOmsFee : 0}
                    placeholder="0"
                    prefix="$"
                    setValue={(value) => {
                      subscriptionFee.amount.setValue(value);
                    }}
                    step={1}
                    value={subscriptionFee.amount.value}
                  />
                </Flex>
              )}
            </Flex>
            <Flex dir="column" gap="lg">
              <RedoToggle
                disabled={wmsToggleDisabled}
                label="Enable WMS monthly subscription fee"
                setValue={(value) => {
                  wmsSubscriptionFee.turnedOnAt.setValue(
                    value ? new Date() : undefined,
                  );
                  if (!value) {
                    wmsSubscriptionFee.amount.setValue(0);
                  }
                }}
                size="sm"
                value={!!wmsSubscriptionFee.turnedOnAt.value}
              />
              {!!wmsSubscriptionFee.turnedOnAt.value && (
                <Flex dir="column" gap="md">
                  <Flex align="center" dir="row" gap="md">
                    <Text fontSize="sm">Begin billing on:</Text>
                    <RedoSingleDatePicker
                      buttonSize="sm"
                      date={dateToPlainDate(
                        toDate(wmsSubscriptionFee.turnedOnAt.value),
                      )}
                      minDate={getStartOfCurrentBillingPeriod()}
                      setDate={(date) => {
                        wmsSubscriptionFee.turnedOnAt.setValue(
                          plainDateToDate(date),
                        );
                      }}
                    />
                  </Flex>
                  {isDateInPast(initialState.initialWmsTurnedOnAt) &&
                    wmsSubscriptionFee.turnedOnAt.value &&
                    toDate(wmsSubscriptionFee.turnedOnAt.value).getTime() !==
                      toDate(initialState.initialWmsTurnedOnAt!).getTime() && (
                      <Text fontSize="xs" textColor="error">
                        Original start date was{" "}
                        {toDate(
                          initialState.initialWmsTurnedOnAt!,
                        ).toLocaleDateString()}
                      </Text>
                    )}
                  <RedoIncrementDecrement
                    disabled={wmsToggleDisabled}
                    label="WMS Monthly Subscription Fee"
                    min={wmsToggleDisabled ? initialState.initialWmsFee : 0}
                    placeholder="0"
                    prefix="$"
                    setValue={(value) => {
                      wmsSubscriptionFee.amount.setValue(value);
                    }}
                    step={1}
                    value={wmsSubscriptionFee.amount.value}
                  />
                </Flex>
              )}
            </Flex>
          </Flex>

          <Flex
            dir="column"
            gap="md"
            style={{
              borderTop: "1px solid var(--redo-colors-border-border-secondary)",
              paddingTop: "var(--redo-spacing-xl)",
            }}
          >
            <Text fontSize="sm" fontWeight="semibold">
              Next Billing Preview
            </Text>
            {billingPreviewLoad.pending ? (
              <Text fontSize="sm" textColor="tertiary">
                Loading billing preview...
              </Text>
            ) : billingPreviewLoad.error ? (
              <Text fontSize="sm" textColor="error">
                Error loading billing preview
              </Text>
            ) : !billingPreview ? (
              <Text fontSize="sm" textColor="tertiary">
                No billing preview available
              </Text>
            ) : !billingPreview.hasShipped ? (
              <Text fontSize="sm" textColor="tertiary">
                No shipments this month - fees will be charged when team ships
              </Text>
            ) : billingPreview.omsCharges.length === 0 &&
              billingPreview.wmsCharges.length === 0 ? (
              <Text fontSize="sm" textColor="tertiary">
                {billingPreview.noChargesReason || "No charges scheduled"}
              </Text>
            ) : (
              <Flex dir="column" gap="sm">
                <Text fontSize="sm" textColor="secondary">
                  On {formatDate(billingPreview.nextBillingDate)}:
                </Text>
                {billingPreview.omsCharges.map((charge, i) => (
                  <Flex justify="space-between" key={`oms-${i}`}>
                    <Text fontSize="sm" textColor="tertiary">
                      {charge.description}
                    </Text>
                    <Text fontSize="sm">${charge.amount.toFixed(2)}</Text>
                  </Flex>
                ))}
                {billingPreview.wmsCharges.map((charge, i) => (
                  <Flex justify="space-between" key={`wms-${i}`}>
                    <Text fontSize="sm" textColor="tertiary">
                      {charge.description}
                    </Text>
                    <Text fontSize="sm">${charge.amount.toFixed(2)}</Text>
                  </Flex>
                ))}
                <Flex
                  justify="space-between"
                  style={{
                    borderTop:
                      "1px solid var(--redo-colors-border-border-secondary)",
                    paddingTop: "var(--redo-spacing-sm)",
                  }}
                >
                  <Text fontSize="sm" fontWeight="semibold">
                    Total
                  </Text>
                  <Text fontSize="sm" fontWeight="semibold">
                    ${billingPreview.totalAmount.toFixed(2)}
                  </Text>
                </Flex>
                {billingPreview.noChargesReason && (
                  <Text fontSize="xs" textColor="tertiary">
                    {billingPreview.noChargesReason}
                  </Text>
                )}
              </Flex>
            )}
          </Flex>
        </Flex>
      </Card>
    );
  },
);
